import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Accessibility as AccessibilityIcon, ArrowRight, Users, ShieldCheck, CheckCircle2, Search, ClipboardList, MessageCircle, Wrench } from 'lucide-react';
const ACCESSIBILITE_STEPS = [{
  title: 'Identification des besoins',
  description: "Échange confidentiel avec le référent handicap avant ou lors de l'inscription pour évaluer les besoins spécifiques.",
  icon: <Search className="w-6 h-6" data-id="element-564" data-mp="2yxoy13qijbkt" />
}, {
  title: "Plan d'accompagnement individualisé",
  description: 'Définition des aménagements pédagogiques, techniques ou organisationnels nécessaires.',
  icon: <ClipboardList className="w-6 h-6" data-id="element-565" data-mp="e7zg2huij9nw" />
}, {
  title: 'Réunion de concertation',
  description: "Implication de l'équipe pédagogique et, si besoin, de l'entreprise d'accueil pour assurer la cohérence du parcours.",
  icon: <MessageCircle className="w-6 h-6" data-id="element-566" data-mp="2zi4vgatz3sfe" />
}, {
  title: 'Déploiement des aménagements',
  description: 'Mise en place effective des solutions (tiers temps, matériel adapté, supports spécifiques, etc.).',
  icon: <Wrench className="w-6 h-6" data-id="element-567" data-mp="26tz8rijcztcx" />
}];
export function Accessibilite() {
  return <div className="flex flex-col w-full" data-id="element-568" data-mp="2bsajdvhha7w7">
      <PageHeader title="Accessibilité et Handicap" subtitle="Zillenium Alternance s'engage pour un accueil inclusif et la réussite de tous les apprenants." data-id="element-569" data-mp="2btbshuexokba" />

      <div className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-570" data-mp="2ljexyu38rqqi">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-571" data-mp="1o37q1qdheqlv" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-572" data-mp="1t0hqe12esmoh">
          {/* Engagement */}
          <section className="flex flex-col md:flex-row gap-10 items-center bg-white shadow-xl p-8 md:p-12 rounded-3xl mb-24 border-none group hover:-translate-y-2 transition-transform duration-300" data-id="element-573" data-mp="1l01wlg71s3yt">
            <div className="w-24 h-24 bg-lavande rounded-3xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-574" data-mp="1uqmuf1ewd4go">
              <AccessibilityIcon className="w-12 h-12 text-brand-orange animate-soft-bounce" data-id="element-575" data-mp="121z5mvuxvwyd" />
            </div>
            <div data-id="element-576" data-mp="x47fa69y0iwa">
              <h2 className="text-3xl font-extrabold font-heading text-primary-dark mb-4" data-id="element-577" data-mp="x4pftf3n1ja0">
                Notre engagement d'accueil inclusif
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed" data-id="element-578" data-mp="1712aogax636c">
                Nous mettons tout en œuvre pour rendre nos formations
                accessibles aux personnes en situation de handicap. Notre
                objectif est de garantir l'égalité des chances en adaptant nos
                parcours de formation et nos modalités d'évaluation aux besoins
                spécifiques de chacun.
              </p>
            </div>
          </section>

          {/* Processus */}
          <section className="mb-24" data-id="element-579" data-mp="2h03ciinlz7t8">
            <SectionHeading title="Le processus d'accompagnement" className="mb-12" data-id="element-580" data-mp="2h1628n97347k" />
            <StepList steps={ACCESSIBILITE_STEPS} data-id="element-581" data-mp="2l39j5remv108" />
          </section>

          <div className="grid md:grid-cols-2 gap-10 mb-24" data-id="element-582" data-mp="38rawreqo57mi">
            {/* Suivi */}
            <Card className="border-none shadow-xl bg-white hover:-translate-y-2 transition-transform duration-300 group" data-id="element-583" data-mp="3doj40kaa3r8z">
              <div className="h-2 w-full bg-primary" data-id="element-584" data-mp="2zy7camhlvuid" />
              <CardContent className="p-8" data-id="element-585" data-mp="33z9cb5jckale">
                <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6 flex items-center gap-4" data-id="element-586" data-mp="33y7wknrdyzxc">
                  <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-587" data-mp="3rnsinfiu6dau">
                    <CheckCircle2 className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-588" data-mp="28t1wwgh2a2j0" />
                  </div>
                  Suivi du parcours
                </h3>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed" data-id="element-589" data-mp="1ytkuc7x3w4r9">
                  Un suivi régulier est assuré tout au long de la formation avec
                  3 rendez-vous annuels dédiés :
                </p>
                <ul className="space-y-4" data-id="element-590" data-mp="2rhgp37347mle">
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-591" data-mp="2rkcm8t75278s">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" data-id="element-592" data-mp="2mk70ftnjveym"></div>
                    <span className="font-bold text-primary-dark" data-id="element-593" data-mp="2hqvuduqkb27r">
                      En début de parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-594" data-mp="3a99g4vymixqh">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '0.5s'
                  }} data-id="element-595" data-mp="35hrfya4fd16k"></div>
                    <span className="font-bold text-primary-dark" data-id="element-596" data-mp="wz9nldg4epwo">
                      À mi-parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-597" data-mp="15zxsninjdfxf">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '1s'
                  }} data-id="element-598" data-mp="1ayisvi2sfzlv"></div>
                    <span className="font-bold text-primary-dark" data-id="element-599" data-mp="1kpluqm3jkim7">
                      En fin de parcours
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contacts & Partenaires */}
            <div className="space-y-10" data-id="element-600" data-mp="1yntx59jckc4r">
              <Card className="bg-primary-dark text-white border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group" data-id="element-601" data-mp="1yoljdtr20xid">
                <div className="h-2 w-full bg-primary-accent" data-id="element-602" data-mp="3ps5c902wp7g7" />
                <CardContent className="p-8" data-id="element-603" data-mp="3ptyiwfdirro7">
                  <h3 className="text-2xl font-extrabold font-heading mb-6 flex items-center gap-4" data-id="element-604" data-mp="8bdq5yihngeg">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-605" data-mp="3kf56lmk1u4v">
                      <Users className="w-6 h-6 text-lavande animate-soft-float" data-id="element-606" data-mp="haikstqxiplc" />
                    </div>
                    Vos référents handicap
                  </h3>
                  <p className="text-lg text-lavande mb-6 leading-relaxed" data-id="element-607" data-mp="r9gjuqlze1il">
                    Nos référents sont à votre écoute pour construire avec vous
                    votre parcours de formation.
                  </p>
                  <div className="space-y-3 mb-8" data-id="element-608" data-mp="2t24pt7q0f322">
                    <div className="font-bold text-xl" data-id="element-609" data-mp="2o3b8t5biktx5">Samir MOKEDDEM</div>
                    <div className="font-bold text-xl" data-id="element-610" data-mp="13kr0frcst0qt">Fadma JIBJIB</div>
                  </div>
                  <a href="mailto:contact@zilleniumalternance.fr" className="text-brand-orange font-bold hover:text-white transition-colors underline underline-offset-4 text-lg" data-id="element-611" data-mp="13n3ie4ik3b3v">
                    Contacter les référents
                  </a>
                </CardContent>
              </Card>

              <div className="bg-white p-8 rounded-3xl shadow-lg border-none" data-id="element-612" data-mp="ktgc9hygj22m">
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-4" data-id="element-613" data-mp="ptm6n5w432oe">
                  Nos partenaires relais
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed" data-id="element-614" data-mp="ay66qk8uock3">
                  Nous travaillons en étroite collaboration avec les acteurs
                  spécialisés du territoire :
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-615" data-mp="2a97gpug3wsi5">
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-616" data-mp="2a8nvo4v5tnzw">
                    AGEFIPH BFC
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-617" data-mp="t0hhfianz8s9">
                    Mission Locale
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-618" data-mp="o7oft3ahsgke">
                    Cap Emploi
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-619" data-mp="edelhg1zp871">
                    MDPH
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Droits & RGPD */}
          <section className="space-y-10 bg-muted p-8 md:p-12 rounded-3xl" data-id="element-620" data-mp="clmai0qtoxl">
            <div data-id="element-621" data-mp="1zp67h4estpqk">
              <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6" data-id="element-622" data-mp="1zpps4svu2s8a">
                Les droits de l'apprenant
              </h3>
              <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground marker:text-brand-orange" data-id="element-623" data-mp="1uqwaco9ikf2b">
                <li data-id="element-624" data-mp="227jcyk2ye2a7">Droit à la non-discrimination à l'accès à la formation.</li>
                <li data-id="element-625" data-mp="276w1d99h3hew">
                  Droit à l'aménagement des épreuves d'examen (tiers temps,
                  assistance, etc.).
                </li>
                <li data-id="element-626" data-mp="2gz1r0cc95mqx">Droit à l'adaptation du poste de travail en entreprise.</li>
                <li data-id="element-627" data-mp="1jarqk1s4771x">Droit à la confidentialité des informations médicales.</li>
              </ul>
            </div>

            <div className="flex gap-6 items-start bg-white p-8 rounded-2xl shadow-sm border-none group" data-id="element-628" data-mp="1o9ba5vj8k6r4">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-629" data-mp="1t8o93endt4a2">
                <ShieldCheck className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-630" data-mp="1y5xu8i3qmts3" />
              </div>
              <p className="text-muted-foreground leading-relaxed" data-id="element-631" data-mp="34q75oo6gi8zn">
                <strong className="text-primary-dark font-bold text-lg block mb-2" data-id="element-632" data-mp="39luuv3kxv255">
                  Confidentialité (RGPD)
                </strong>
                Les informations relatives à votre situation de handicap sont
                strictement confidentielles. Elles ne sont partagées avec
                l'équipe pédagogique ou l'entreprise qu'avec votre accord
                explicite, et uniquement dans le but de mettre en place les
                aménagements nécessaires.
              </p>
            </div>

            <div className="text-center pt-8" data-id="element-633" data-mp="36sjn19x4dvk7">
              <Button size="lg" className="group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-7 shadow-xl" asChild data-id="element-634" data-mp="3araaepbs9yu4">
                <Link to="/candidater" data-id="element-635" data-mp="3kqhx434er5tw">
                  Remplir le formulaire etudiant
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-636" data-mp="v1flodn16ylf" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>;
}